import { useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { useAppLayout } from '@/components/AppLayout/AppLayoutProvider'
import UIIcon from '@/components/UI/UIIcon'

export type SummaryPartSliderCursorProps = {
  visible: boolean
  setIsMouseHasMoved: (value: boolean) => void
}

export default function SummaryPartSliderCursor({ visible, setIsMouseHasMoved }: SummaryPartSliderCursorProps) {
  const { t } = useTranslation()
  const { root } = useAppLayout()

  const [mouse, setMouse] = useState({ x: 0, y: 0 })
  const [rotation, setRotation] = useState(0)

  useLayoutEffect(() => {
    const onMouseMove = (event: MouseEvent) => {
      setMouse({ x: event.clientX, y: event.clientY })
      setRotation((event.clientX / window.innerWidth) * 45 - 22.5)

      setIsMouseHasMoved(true)
    }
    window.addEventListener('mousemove', onMouseMove)

    return () => {
      window.removeEventListener('mousemove', onMouseMove)
    }
  }, [setMouse, setIsMouseHasMoved])

  return (
    root.current &&
    createPortal(
      <>
        <div
          aria-hidden='true'
          className='SummaryPartSliderCursor pointer-events-none fixed z-29 -top-52 -left-52 transition-transform duration-75 ease-out-quad'
          style={{ transform: `translate(${mouse.x}px, ${mouse.y}px)` }}
        >
          <div className='relative' style={{ transform: `rotate(${rotation}deg)` }}>
            <UIIcon
              icon='dragBackgroundCursor'
              className={`w-104 h-104 text-accent1 transition-transform duration-500 ease-in-out-quad ${visible ? 'scale-100 rotate-0' : 'scale-0 rotate-360 duration-200'}`}
            />
            {/* <svg viewBox="0 0 120 120" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-120 h-120">
            <circle cx="60" cy="60" r="60" fill="none" stroke="none" />
          </svg> */}

            <div
              className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-160 h-160 transition-transform duration-500 ease-in-out-quad ${visible ? 'scale-100 rotate-0' : 'scale-0 rotate-12 duration-200'}`}
            >
              <svg className='relative w-full h-full -rotate-10' viewBox='0 0 512 512'>
                <path id='textcircle' d='M56,256a200,200 0 1,0 400,0a200,200 0 1,0 -400,0' fill='none' />
                <text
                  dy='35'
                  dx='90'
                  className='antialiased font-body leading-relaxed tracking-wide text-[52px] font-semibold fill-black-blue'
                >
                  <textPath href='#textcircle'>{t('summary.drag')}</textPath>
                </text>
              </svg>
            </div>
          </div>
        </div>
        <div
          aria-hidden='true'
          className='SummaryPartSliderCursor pointer-events-none fixed z-29 -top-52 -left-52'
          style={{ transform: `translate(${mouse.x}px, ${mouse.y}px)` }}
        >
          <UIIcon
            icon='previous'
            className={`position absolute top-1/2 -mt-10 left-1/2 -ml-30 w-20 h-20 transition-all duration-500 ease-in-out-quad ${visible ? 'scale-100 opacity-100' : 'scale-50 opacity-0 translate-x-12 duration-200'}`}
          />
          <UIIcon
            icon='dragCenterCursor'
            className={`position relative top-0 left-0 w-104 h-104 fill-black-blue transition-all duration-500 ease-in-out-quad ${visible ? 'scale-100 opacity-100' : 'scale-50 opacity-0 duration-200'}`}
          />
          <UIIcon
            icon='next'
            className={`position absolute top-1/2 -mt-10 right-1/2 -mr-30 w-20 h-20 transition-all duration-500 ease-in-out-quad ${visible ? 'scale-100 opacity-100' : 'scale-50 opacity-0 -translate-x-12 duration-200'}`}
          />
        </div>
      </>,
      root.current
    )
  )
}
